import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    CheckCircle2,
    Circle,
    Clock,
    MinusCircle,
    Sparkles,
    Loader2,
    Wand2,
    Inbox,
    AlertCircle,
    RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
    analyzeTender,
    generateAnnex,
    updateChecklistItem,
} from "@/lib/api";

const STATUS_META = {
    pending: { label: "Pendiente", color: "slate", icon: Circle },
    in_progress: { label: "En curso", color: "amber", icon: Clock },
    done: { label: "Listo", color: "emerald", icon: CheckCircle2 },
    n_a: { label: "N/A", color: "slate", icon: MinusCircle },
};

const STATUS_ORDER = ["pending", "in_progress", "done", "n_a"];

const STATUS_STYLES = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const ChecklistView = ({ tender, onChange }) => {
    const [savingKey, setSavingKey] = useState(null);
    const [generatingKey, setGeneratingKey] = useState(null);
    const [reanalyzing, setReanalyzing] = useState(false);

    const items = tender.checklist || [];
    const totalRequired = items.filter((i) => i.required).length;
    const doneRequired = items.filter(
        (i) => i.required && i.status === "done"
    ).length;
    const progressPct = totalRequired
        ? Math.round((doneRequired / totalRequired) * 100)
        : 0;

    // Group items by sobre, preserving the order in which sobres first appear.
    const grouped = useMemo(() => {
        const map = new Map();
        items.forEach((it) => {
            const sobre = it.sobre || "Sobre único";
            if (!map.has(sobre)) map.set(sobre, []);
            map.get(sobre).push(it);
        });
        return Array.from(map.entries()).map(([sobre, list]) => ({
            sobre,
            items: list,
        }));
    }, [items]);

    const updateItem = async (key, patch) => {
        setSavingKey(key);
        try {
            const updated = await updateChecklistItem(tender.id, key, patch);
            onChange?.(updated);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al actualizar");
        } finally {
            setSavingKey(null);
        }
    };

    const cycleStatus = (item) => {
        const idx = STATUS_ORDER.indexOf(item.status);
        const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
        updateItem(item.key, { status: next });
    };

    const generateAnnexFromItem = async (item) => {
        setGeneratingKey(item.key);
        try {
            const updated = await generateAnnex(tender.id, item.label, "");
            // Find the new annex and link it to the item
            const newAnnex = (updated.annexes || []).find(
                (a) => (a.title || "").trim().toLowerCase() === item.label.trim().toLowerCase()
            );
            if (newAnnex) {
                const updated2 = await updateChecklistItem(tender.id, item.key, {
                    annex_id: newAnnex.id,
                    status: "done",
                });
                onChange?.(updated2);
            } else {
                onChange?.(updated);
            }
            toast.success(`Anexo "${item.label}" generado`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo generar el anexo"
            );
        } finally {
            setGeneratingKey(null);
        }
    };

    const reanalyze = async () => {
        setReanalyzing(true);
        try {
            const updated = await analyzeTender(tender.id);
            onChange?.(updated);
            toast.success("Checklist generada");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo generar la checklist"
            );
        } finally {
            setReanalyzing(false);
        }
    };

    // Auto-generate the checklist the first time this tender's view is opened
    // and no items exist yet. We use a ref so we don't trigger multiple parallel
    // analyses if the component re-renders.
    const autoTriggeredRef = useRef(false);
    useEffect(() => {
        if (
            !autoTriggeredRef.current &&
            !reanalyzing &&
            (tender.checklist || []).length === 0 &&
            tender.id
        ) {
            autoTriggeredRef.current = true;
            reanalyze();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tender.id]);

    return (
        <div className="space-y-5" data-testid="checklist-view">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl font-black">
                        Checklist de la oferta
                    </h2>
                    <p className="text-sm text-ink-500 mt-1 font-medium max-w-3xl">
                        La IA ha extraído del pliego qué documento va en cada sobre. Marca el
                        estado de cada uno, o pulsa "Generar anexo" para que la IA lo redacte
                        con tus datos.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={reanalyze}
                    disabled={reanalyzing}
                    className="rounded-xl"
                    data-testid="reanalyze-checklist-btn"
                >
                    {reanalyzing ? (
                        <>
                            <Loader2 size={13} className="mr-1.5 animate-spin" />
                            {items.length === 0 ? "Generando…" : "Analizando…"}
                        </>
                    ) : (
                        <>
                            <RefreshCw size={13} className="mr-1.5" />
                            {items.length === 0 ? "Generar checklist" : "Regenerar checklist"}
                        </>
                    )}
                </Button>
            </div>

            {items.length === 0 ? (
                reanalyzing ? (
                    <div
                        className="bg-brand-50/60 border border-brand-200 text-brand-800 rounded-2xl p-6 flex items-start gap-3"
                        data-testid="checklist-auto-generating"
                    >
                        <Loader2 size={20} className="shrink-0 mt-0.5 animate-spin" />
                        <div>
                            <div className="font-display text-base font-black mb-1">
                                Generando checklist con IA…
                            </div>
                            <div className="text-sm font-medium">
                                Analizando el pliego para extraer la documentación exigida en
                                cada sobre / archivo electrónico. Esto suele tardar unos
                                segundos.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-6 flex items-start gap-3"
                        data-testid="checklist-empty"
                    >
                        <AlertCircle size={20} className="shrink-0 mt-0.5" />
                        <div>
                            <div className="font-display text-base font-black mb-1">
                                La IA no encontró el detalle por sobres
                            </div>
                            <div className="text-sm font-medium">
                                Pulsa "Generar checklist" para que la IA vuelva a extraer la
                                documentación exigida en cada sobre/archivo electrónico.
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <>
                    {/* Progress bar */}
                    <div className="bg-white border border-ink-100 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-bold text-ink-950">
                                Progreso de documentación obligatoria
                            </div>
                            <div className="text-sm font-mono font-bold text-brand-600">
                                {doneRequired}/{totalRequired} ({progressPct}%)
                            </div>
                        </div>
                        <Progress value={progressPct} className="h-2" />
                    </div>

                    {/* Sobres */}
                    <div className="space-y-4">
                        {grouped.map(({ sobre, items: list }) => {
                            const reqInBlock = list.filter((i) => i.required).length;
                            const doneInBlock = list.filter(
                                (i) => i.required && i.status === "done"
                            ).length;
                            return (
                                <div
                                    key={sobre}
                                    className="bg-white border border-ink-100 rounded-2xl overflow-hidden"
                                    data-testid={`sobre-${sobre.slice(0, 20)}`}
                                >
                                    <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-ink-100 bg-gradient-to-r from-brand-50/60 to-cyan-50/40">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shrink-0">
                                                <Inbox size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-display font-black text-ink-950 text-base">
                                                    {sobre}
                                                </div>
                                                <div className="text-xs text-ink-500 font-medium mt-0.5">
                                                    {list.length} documento(s) ·{" "}
                                                    {doneInBlock}/{reqInBlock} listos
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="divide-y divide-ink-100">
                                        {list.map((it) => (
                                            <ChecklistRow
                                                key={it.key}
                                                item={it}
                                                isSaving={savingKey === it.key}
                                                isGenerating={generatingKey === it.key}
                                                onCycleStatus={() => cycleStatus(it)}
                                                onGenerateAnnex={() =>
                                                    generateAnnexFromItem(it)
                                                }
                                                hasAnnex={!!it.annex_id}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const ChecklistRow = ({
    item,
    isSaving,
    isGenerating,
    onCycleStatus,
    onGenerateAnnex,
    hasAnnex,
}) => {
    const meta = STATUS_META[item.status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
        <div
            className="px-5 py-3.5 flex items-start gap-4"
            data-testid={`checklist-item-${item.key}`}
        >
            <button
                onClick={onCycleStatus}
                disabled={isSaving}
                className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-1 transition-colors ${
                    STATUS_STYLES[meta.color] || STATUS_STYLES.slate
                } hover:scale-[1.02]`}
                title="Click para cambiar estado"
                data-testid={`status-toggle-${item.key}`}
            >
                {isSaving ? (
                    <Loader2 size={11} className="animate-spin" />
                ) : (
                    <Icon size={11} />
                )}
                {meta.label}
            </button>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-ink-950 flex items-center gap-2 flex-wrap">
                    <span>{item.label}</span>
                    {item.required && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded-full">
                            Obligatorio
                        </span>
                    )}
                    {hasAnnex && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            Anexo generado
                        </span>
                    )}
                </div>
                {item.description && (
                    <div className="text-xs text-ink-500 mt-0.5">
                        {item.description}
                    </div>
                )}
            </div>
            <Button
                size="sm"
                variant={hasAnnex ? "outline" : "default"}
                onClick={onGenerateAnnex}
                disabled={isGenerating || isSaving}
                className={`shrink-0 rounded-xl ${
                    hasAnnex
                        ? ""
                        : "bg-brand-600 hover:bg-brand-700 text-white font-bold"
                }`}
                data-testid={`generate-from-item-${item.key}`}
            >
                {isGenerating ? (
                    <>
                        <Loader2 size={13} className="mr-1.5 animate-spin" />
                        Generando…
                    </>
                ) : hasAnnex ? (
                    <>
                        <Sparkles size={13} className="mr-1.5" />
                        Regenerar
                    </>
                ) : (
                    <>
                        <Wand2 size={13} className="mr-1.5" />
                        Generar anexo
                    </>
                )}
            </Button>
        </div>
    );
};

export default ChecklistView;
